// src/components/ProductList.js
import React, { useEffect, useState, useRef, useCallback,useMemo } from 'react';
import { db } from '../firebase'; // Adjust path as needed
import { collection, query, getDocs } from 'firebase/firestore';
import './main.css'; // Ensure styles for loader, messages, etc. are here
import { Link } from 'react-router-dom';
// REMOVED: No longer importing SearchBar here

// Accept discount, initialSearchTerm, and products (for category filtering)
const ProductList = ({ initialLimit = 12, increment = 8, customerDiscount, initialSearchTerm = '', products = null }) => {

  // --- State ---
  const [allProducts, setAllProducts] = useState([]);         // Holds ALL products after fetch
  const [filteredProducts, setFilteredProducts] = useState([]); // Holds products matching the current search term
  const [visibleProducts, setVisibleProducts] = useState([]); // Holds the products currently rendered in the grid
  // ** Search term state is managed HERE and used by the input below **
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm); // Initialized by prop
  const [showSuggestions, setShowSuggestions] = useState(false); // State for suggestions dropdown
  const [hasMore, setHasMore] = useState(true);              // Flag: any more items to load?
  const [isLoading, setIsLoading] = useState(true);           // Flag: initial data fetching?
  const [isLoadingMore, setIsLoadingMore] = useState(false);    // Flag: loading next batch on scroll?

  // --- Refs ---
  const observerRef = useRef(null); // Ref for the IntersectionObserver instance
  const loadMoreTriggerRef = useRef(null); // Ref for the DOM element that triggers loading more

  // --- Initial Data Fetch Effect ---
  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);

    // If products are passed as a prop (e.g., from CategoryPage), use them directly
    if (products && Array.isArray(products)) {
      setAllProducts(products);
      setIsLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        const productsRef = collection(db, 'products');
        const q = query(productsRef);
        const querySnapshot = await getDocs(q);
        const productsData = querySnapshot.docs.map(doc => ({
          id: doc.id, // Use Firestore document ID
          ...doc.data(),
        }));

        if (isMounted) {
          setAllProducts(productsData); // Store all products
          setIsLoading(false); // Mark initial load as complete
          // Filtering happens in the next effect based on the initial searchTerm
        }
      } catch (error) {
        console.error('Error fetching initial products:', error);
        if (isMounted) setIsLoading(false);
      }
    };

    fetchData();

    return () => { isMounted = false; }; // Cleanup on unmount
  }, [products]); // Add products as a dependency

  // --- Filtering Effect ---
  // Runs whenever searchTerm, allProducts, initialLimit, or isLoading changes
  useEffect(() => {
    if (isLoading) {
        setFilteredProducts([]); setVisibleProducts([]); setHasMore(false); return;
    }
    if (!allProducts || allProducts.length === 0) {
         setFilteredProducts([]); setVisibleProducts([]); setHasMore(false); return;
    }

    const lowerCaseSearchTerm = searchTerm.toLowerCase().trim(); // Use internal state

    const newlyFiltered = allProducts.filter(product => {
        if (lowerCaseSearchTerm === '') return true; // Show all if search is empty
        const nameMatch = (product["Product name"] || '').toLowerCase().includes(lowerCaseSearchTerm);
        const skuMatch = (product["SKU"]?.toString() || '').includes(lowerCaseSearchTerm); // Check SKU as string
        let akaMatch = false;
        if (product.aka && typeof product.aka === 'string') {
            akaMatch = product.aka.toLowerCase().split(',').map(t => t.trim()).some(t => t.includes(lowerCaseSearchTerm));
        }
        return nameMatch || skuMatch || akaMatch;
    });

    setFilteredProducts(newlyFiltered);
    const initialBatch = newlyFiltered.slice(0, initialLimit);
    setVisibleProducts(initialBatch);
    setHasMore(newlyFiltered.length > initialLimit);
    setIsLoadingMore(false);

  }, [searchTerm, allProducts, initialLimit, isLoading]); // Dependencies


  // --- Suggestion Generation (using internal searchTerm state) ---
  const filteredSuggestions = useMemo(() => {
    if (!searchTerm || !searchTerm.trim() || !allProducts || allProducts.length === 0) {
      return []; // No suggestions if search empty or products not loaded
    }
    const lowerCaseSearchTerm = searchTerm.toLowerCase();
    // Generate suggestions based on *all* products, not just filtered/visible ones
    return allProducts
      .filter((product) => {
        const lowerCaseName = product["Product name"]?.toLowerCase() || '';
        const skuAsString = product["SKU"]?.toString() || '';
        const akaTags = product.aka ? product.aka.split(',').map(tag => tag.trim().toLowerCase()) : [];
        return (
          lowerCaseName.includes(lowerCaseSearchTerm) ||
          skuAsString.includes(lowerCaseSearchTerm) ||
          akaTags.some(tag => tag.includes(lowerCaseSearchTerm))
        );
      })
      .slice(0, 8); // Limit suggestions
  }, [searchTerm, allProducts]); // Depend on internal search term and all products


  // --- Load More Function ---
  const loadMoreProducts = useCallback(() => {
    if (isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    const currentLength = visibleProducts.length;
    const nextLimit = currentLength + increment;
    const nextBatch = filteredProducts.slice(currentLength, nextLimit);
    setTimeout(() => {
      setVisibleProducts(prevVisible => [...prevVisible, ...nextBatch]);
      setHasMore(filteredProducts.length > nextLimit);
      setIsLoadingMore(false);
    }, 250);
  }, [isLoadingMore, hasMore, visibleProducts.length, increment, filteredProducts]);


  // --- Intersection Observer Setup Effect ---
   useEffect(() => {
    if (isLoading) return;
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0];
        if (firstEntry?.isIntersecting && hasMore && !isLoadingMore) {
          loadMoreProducts();
        }
      },
      { threshold: 0.8 }
    );
    const currentTriggerElement = loadMoreTriggerRef.current;
    const currentObserver = observerRef.current;
    if (currentTriggerElement) currentObserver.observe(currentTriggerElement);
    return () => { if (currentObserver) currentObserver.disconnect(); };
  }, [loadMoreProducts, hasMore, isLoadingMore, isLoading]);


  // --- Render Logic ---
  if (isLoading) {
    return <div className="loading-message">Loading Products...</div>;
  }

  return (
    <div className="product-list-container">

      {/* == Search Input and Suggestions directly within ProductList == */}
      <div className="search-container-internal"> {/* Use a specific container class */}
        <input
          type="search"
          className="search search-internal-input" // Specific class
          placeholder='Search products by Name or SKU...'
          value={searchTerm} // Value is from ProductList's state
          onChange={(e) => {
            setSearchTerm(e.target.value); // Update ProductList's state directly
            setShowSuggestions(e.target.value.trim().length > 0); // Show/hide suggestions
          }}
          onFocus={() => { if (searchTerm && searchTerm.trim()) setShowSuggestions(true); }}
          onBlur={() => { setTimeout(() => setShowSuggestions(false), 200); }}
          aria-label="Search Products within this list"
        />
        {/* Suggestions Dropdown */}
        {showSuggestions && filteredSuggestions.length > 0 && (
          <ul className="search-suggestions search-suggestions-internal"> {/* Specific class */}
            {filteredSuggestions.map((suggestion) => (
              <li key={suggestion.id}>
                <Link
                  to={`/products/${suggestion.id}`} // Link uses Firestore ID
                  onClick={() => {
                    setSearchTerm(''); // Clear search term state HERE
                    setShowSuggestions(false); // Hide suggestions
                  }}
                >
                  {suggestion["Product name"]}
                  <span className="suggestion-sku"> (SKU: {suggestion.SKU})</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
      {/* == End of Search Input section == */}


      {/* Product Grid - Renders based on 'visibleProducts' state */}
      {visibleProducts.length > 0 ? (
        <div className="box-container">
          {visibleProducts.map(product => {
            // Price calculations
            const sellingPrice = typeof product.Rate === 'number' ? product.Rate : 0;
            const discountedSellingPrice = (sellingPrice * (1 - customerDiscount / 100)).toFixed(2);
            
            const displayPrice = customerDiscount > 0 ? discountedSellingPrice : sellingPrice.toFixed(2);
            const originalPriceDisplay = sellingPrice.toFixed(2);

            return (
              <div className="box" key={product.id}>
                {/* Discount */}
                {customerDiscount > 0 && <span className="discount">-{customerDiscount}%</span>}
                {/* Image */}
                <div className="image">
                   <img src={product.image || '/placeholder.png'} alt={product["Product name"] || 'Product Image'} loading="lazy" onError={(e) => { e.target.onerror = null; e.target.src = '/placeholder.png'; }} />
                   <div className="icons">
                       <Link target='_blank' rel="noopener noreferrer" to={`https://wa.me/919911192881?text=...`} className="fas fa-bag-shopping" title="Request Sample"></Link>
                       <Link to={`/products/${product.id}`} className="cart-btn" title="View Product Details">View Product</Link>
                       <a href="#" className="fas fa-share" title="Copy Share Link" onClick={(e) => { /* ... */ }}></a>
                   </div>
                </div>
                {/* Content */}
                <div className="content">
                   <h3>{`AMCOUCH ${product["Product name"] || 'Product'}`}</h3>
                    {product.SKU && <p className='des sku-id'>SKU: {product.SKU}</p>}
                    <div className="raw"> Raw foam prices</div>
                   
                    {(product.L || product.B || product.H) && <p className='des dimensions'> Dims: {product.L ?? '?'}L x {product.B ?? '?'}B x {product.H ?? '?'}H (in) </p>}
                    <div className="price">
                        ₹{displayPrice}
                        {customerDiscount > 0 && sellingPrice > 0 && <span className="original-price">₹{originalPriceDisplay}</span>}
                    </div>
                    <Link to={`/products/${product.id}`} className="btn view-product-btn"> View Product </Link>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        // No Products Message
        !isLoading && (filteredProducts.length === 0 && searchTerm !== '' ?
            <div className="no-products-message">No products found matching "{searchTerm}".</div>
            :
            filteredProducts.length === 0 && searchTerm === '' ?
            <div className="no-products-message">No products available.</div>
            : null
        )
      )}

      {/* Load More Trigger */}
      {hasMore && (
        <div ref={loadMoreTriggerRef} className="load-more-trigger">
          {isLoadingMore && <p>Loading more products...</p>}
        </div>
      )}

      {/* End of List Message */}
      {!hasMore && !isLoading && visibleProducts.length > 0 && (
        <div className="end-of-list-message">
          <p>You've reached the end of the product list.</p>
        </div>
      )}
    </div>
  );
};

export default ProductList;